import { useRef, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

// Same language options as the Analyze FIR / Analyze Statement screens.
export const ANSWER_LANGUAGES = [
  'English', 'Hindi', 'Gujarati', 'Marathi', 'Bengali', 'Odia',
  'Telugu', 'Tamil', 'Kannada', 'Malayalam', 'Punjabi', 'Urdu',
];

// The user's native/working language comes from the JWT's `language` claim
// (also mirrored into localStorage at login). Falls back to English if it is
// missing or not one of the supported languages.
export const getUserLanguage = () => {
  let lang = '';
  try {
    const token = localStorage.getItem('jwtToken');
    if (token) lang = jwtDecode(token)?.language || '';
  } catch {
    // fall through to localStorage
  }
  if (!lang) lang = localStorage.getItem('language') || '';
  return ANSWER_LANGUAGES.find((l) => l.toLowerCase() === String(lang).trim().toLowerCase()) || 'English';
};

// Shared state/logic for the "Record Answer" screens (desktop popups + mobile
// screen). `post(path, body)` must return a promise resolving to { data }.
export const useAnswerTranslation = (post) => {
  const [answerLanguage, setAnswerLanguage] = useState(getUserLanguage);
  const [translationLanguage, setTranslationLanguage] = useState(getUserLanguage);
  const [translation, setTranslation] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(null);
  const sourceRef = useRef(null); // { text, language, target } the current translation was made from

  const needsTranslation = answerLanguage !== translationLanguage;

  const translate = async (answerText) => {
    const text = (answerText || '').trim();
    if (!text) return '';
    setIsTranslating(true);
    setTranslateError(null);
    try {
      const response = await post('/translate', {
        text,
        language: answerLanguage,
        targetLanguage: translationLanguage,
      });
      const translated = response?.data?.translatedText || '';
      sourceRef.current = { text, language: answerLanguage, target: translationLanguage };
      setTranslation(translated);
      return translated;
    } catch {
      setTranslateError('Failed to translate the answer. Please try again.');
      throw new Error('translate failed');
    } finally {
      setIsTranslating(false);
    }
  };

  // Returns the translation to save: reuses the current one if it still
  // matches the answer text and languages, otherwise translates first.
  const ensureTranslation = async (answerText) => {
    if (!needsTranslation) return '';
    const text = (answerText || '').trim();
    const src = sourceRef.current;
    if (translation && src && src.text === text && src.language === answerLanguage && src.target === translationLanguage) {
      return translation;
    }
    return translate(text);
  };

  const reset = () => {
    setAnswerLanguage(getUserLanguage());
    setTranslationLanguage(getUserLanguage());
    setTranslation('');
    setTranslateError(null);
    setIsTranslating(false);
    sourceRef.current = null;
  };

  return {
    answerLanguage, setAnswerLanguage,
    translationLanguage, setTranslationLanguage,
    translation, setTranslation,
    isTranslating, translateError, setTranslateError,
    needsTranslation, translate, ensureTranslation, reset,
  };
};

// Translation to show under the idx-th recorded answer of a question: only when
// that answer was given in a language other than the user's native/working
// language and a translation was saved with it. (answerLanguage /
// answerTranslation are parallel arrays to `answer`.)
export const getAnswerTranslation = (question, idx) => {
  const translation = question?.answerTranslation?.[idx];
  const answerLang = question?.answerLanguage?.[idx];
  if (!translation || !String(translation).trim() || !answerLang) return '';
  if (String(answerLang).trim().toLowerCase() === getUserLanguage().toLowerCase()) return '';
  return translation;
};
